/**
 * Sound Effects for ThreadHeads Production App
 *
 * Sound Frequency Rationale:
 * - Success: A5 (880Hz) + E6 (1320Hz) - Musical perfect fifth interval, rising = positive
 * - Error: A4 (440Hz) + E4 (330Hz) - Descending fifth interval, falling = negative
 *
 * Frequencies chosen for:
 * - Audibility in noisy warehouse (500-1500Hz cuts through ambient noise)
 * - Musical intervals (perfect fifth) sound intentional, not accidental beep
 * - High-pitch success (more piercing), low-pitch error (more ominous)
 */

let audioContext: AudioContext | null = null;
let activeSounds = 0;
const MAX_CONCURRENT_SOUNDS = 3;
const SOUND_TIMEOUT_MS = 500; // Force cleanup after this duration

function getAudioContext(): AudioContext | null {
  try {
    if (!audioContext) {
      audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    }

    // Resume context if suspended (browser autoplay policy)
    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {
        // Silently fail - audio not critical
      });
    }

    return audioContext;
  } catch {
    return null;
  }
}

/**
 * Trigger visual fallback when audio fails or is unavailable.
 * Flashes green/red border on scanner input element.
 */
function triggerVisualFallback(type: 'success' | 'error'): void {
  const scanner = document.querySelector('[data-scanner-input]');
  if (scanner) {
    const className = type === 'success' ? 'flash-success' : 'flash-error';
    scanner.classList.add(className);
    setTimeout(() => scanner.classList.remove(className), 300);
  }
}

/**
 * Play success beep - rising two-tone pattern (A5 → E6).
 * Used for successful scans and positive actions.
 */
export function playSuccessBeep(): void {
  // Limit concurrent sounds to prevent audio node exhaustion
  if (activeSounds >= MAX_CONCURRENT_SOUNDS) {
    triggerVisualFallback('success');
    return;
  }

  const ctx = getAudioContext();
  if (!ctx) {
    triggerVisualFallback('success');
    return;
  }

  try {
    const now = ctx.currentTime;
    activeSounds++;

    // Set timeout fallback to force cleanup (prevents memory leak on audio glitches)
    const cleanupTimeout = setTimeout(() => {
      activeSounds = Math.max(0, activeSounds - 1);
    }, SOUND_TIMEOUT_MS);

    // First beep: A5 (880Hz) - 50ms
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(880, now);
    gain1.gain.setValueAtTime(0.4, now);
    gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.05);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.05);

    // Second beep: E6 (1320Hz) - 50ms after 30ms gap (rising = success)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(1320, now + 0.08);
    gain2.gain.setValueAtTime(0.4, now + 0.08);
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.13);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.08);
    osc2.stop(now + 0.13);

    // Decrement counter when done (normal path)
    osc2.onended = () => {
      clearTimeout(cleanupTimeout);
      activeSounds = Math.max(0, activeSounds - 1);
    };
  } catch {
    activeSounds = Math.max(0, activeSounds - 1);
    triggerVisualFallback('success');
  }
}

/**
 * Play error beep - descending two-tone pattern (A4 → E4).
 * Used for errors, warnings, and attention-needed states.
 */
export function playErrorBeep(): void {
  // Limit concurrent sounds to prevent audio node exhaustion
  if (activeSounds >= MAX_CONCURRENT_SOUNDS) {
    triggerVisualFallback('error');
    return;
  }

  const ctx = getAudioContext();
  if (!ctx) {
    triggerVisualFallback('error');
    return;
  }

  try {
    const now = ctx.currentTime;
    activeSounds++;

    // Set timeout fallback to force cleanup
    const cleanupTimeout = setTimeout(() => {
      activeSounds = Math.max(0, activeSounds - 1);
    }, SOUND_TIMEOUT_MS);

    // First beep: A4 (440Hz) - 120ms
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(440, now);
    gain1.gain.setValueAtTime(0.5, now);  // Louder than success
    gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.12);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.12);

    // Second beep: E4 (330Hz) - 120ms (descending = error)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(330, now + 0.12);
    gain2.gain.setValueAtTime(0.5, now + 0.12);
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.24);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.12);
    osc2.stop(now + 0.24);

    // Decrement counter when done (normal path)
    osc2.onended = () => {
      clearTimeout(cleanupTimeout);
      activeSounds = Math.max(0, activeSounds - 1);
    };
  } catch {
    activeSounds = Math.max(0, activeSounds - 1);
    triggerVisualFallback('error');
  }
}
